import { useCallback, useEffect, useRef, useState } from "react";
import {
  createApplication,
  getApplication,
  listApplications,
  loadDeveloper,
  rotateApplicationCredentials,
  updateApplication,
  type Application,
  type ApplicationDraft,
  type Endpoints,
  type NativeCredentials,
} from "./api";
import {
  authenticateWithPassword,
  completeLogin,
  loadSession,
  logout,
  saveSession,
  startLogin,
  type Developer,
  type Session,
} from "./auth";
import { ApplicationDialog } from "./components/ApplicationDialog";
import { ApplicationOverviewView } from "./components/ApplicationOverviewView";
import { ApplicationSettingsView } from "./components/ApplicationSettingsView";
import { ApplicationsView } from "./components/ApplicationsView";
import { CallbackConfigurationView } from "./components/CallbackConfigurationView";
import { CallbackFailureLogsView } from "./components/CallbackFailureLogsView";
import { ConsoleShell } from "./components/ConsoleShell";
import { NoPermissionPage, NotFoundPage } from "./components/ConsoleStatePages";
import { CredentialDialog } from "./components/CredentialDialog";
import { CredentialsSecurityView } from "./components/CredentialsSecurityView";
import { GroupManagementView } from "./components/GroupManagementView";
import { LoginPage } from "./components/LoginPage";
import { QuickstartView } from "./components/QuickstartView";
import { RetentionPolicyView } from "./components/RetentionPolicyView";
import { ServerMessageSendView } from "./components/ServerMessageSendView";
import { ServerMessagesView } from "./components/ServerMessagesView";
import { SystemAccountManagementView } from "./components/SystemAccountManagementView";
import { UserManagementView } from "./components/UserManagementView";
import { ConfirmDialog } from "./components/ui/ConfirmDialog";
import {
  consoleRoutePath,
  isApplicationScoped,
  legacyConsoleRouteRedirect,
  parseConsoleRoute,
  requiredPermission,
  type ConsoleRoute,
  type ConsoleView,
} from "./routes";

type Confirmation = "rotate-credentials" | "disable-application";

/**
 * 开放平台主入口。浏览器 URL 是页面状态的唯一外部合同，应用选择和权限守卫
 * 均在这里收敛，避免各业务页面各自维护一份容易漂移的上下文。
 */
export function App() {
  const initialRoute = parseConsoleRoute(window.location.pathname === "/callback" ? "/applications" : window.location.pathname);
  const [route, setRoute] = useState<ConsoleRoute>(initialRoute);
  const routeRef = useRef<ConsoleRoute>(initialRoute);
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [developer, setDeveloper] = useState<Developer | null>(() => session?.user ?? null);
  const [applications, setApplications] = useState<readonly Application[]>([]);
  const [selected, setSelected] = useState<Application | null>(null);
  const [endpoints, setEndpoints] = useState<Endpoints | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<"create" | "edit" | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<NativeCredentials | null>(null);

  const showNotice = useCallback((message: string): void => {
    setError(null);
    setNotice(message);
  }, []);
  const showError = useCallback((message: string): void => {
    setNotice(null);
    setError(message);
  }, []);

  const navigate = useCallback((view: ConsoleView, appId?: string | null, replace = false): void => {
    const next: ConsoleRoute = {
      view,
      appId: isApplicationScoped(view) ? appId ?? routeRef.current.appId : null,
    };
    const path = consoleRoutePath(next.view, next.appId);
    if (replace) window.history.replaceState({}, "", path);
    else if (window.location.pathname !== path) window.history.pushState({}, "", path);
    routeRef.current = next;
    setRoute(next);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const [user, apps] = await Promise.all([loadDeveloper(), listApplications()]);
    setDeveloper(user);
    setApplications(apps);
    setSelected((current) => {
      const routedAppId = routeRef.current.appId;
      if (routedAppId !== null) return apps.find((app) => app.appId === routedAppId) ?? null;
      return apps.find((app) => app.appId === current?.appId) ?? apps[0] ?? null;
    });
  }, []);

  useEffect(() => {
    const initialRedirect = legacyConsoleRouteRedirect(window.location.pathname);
    if (initialRedirect !== null) window.history.replaceState({}, "", initialRedirect);

    const onPopState = (): void => {
      const pathname = window.location.pathname;
      const redirect = legacyConsoleRouteRedirect(pathname);
      if (redirect !== null) window.history.replaceState({}, "", redirect);
      const next = parseConsoleRoute(redirect ?? pathname);
      routeRef.current = next;
      setRoute(next);
    };
    window.addEventListener("popstate", onPopState);
    return () => { window.removeEventListener("popstate", onPopState); };
  }, []);

  useEffect(() => {
    let active = true;
    async function initialize(): Promise<void> {
      try {
        if (window.location.pathname === "/callback") {
          const completed = await completeLogin();
          if (!active) return;
          setSession(completed);
          navigate("applications", null, true);
        } else if (loadSession() === null) {
          setLoading(false);
          return;
        }
        await refresh();
      } catch (cause) {
        if (active) showError(errorMessage(cause));
      } finally {
        if (active) setLoading(false);
      }
    }
    void initialize();
    return () => { active = false; };
  }, [navigate, refresh, showError]);

  useEffect(() => {
    if (route.appId === null) return;
    const application = applications.find((candidate) => candidate.appId === route.appId) ?? null;
    setSelected(application);
  }, [applications, route.appId]);

  useEffect(() => {
    if (selected === null) {
      setEndpoints(null);
      return;
    }
    let active = true;
    void getApplication(selected.appId).then((result) => {
      if (!active) return;
      setSelected(result.application);
      setEndpoints(result.endpoints);
    }).catch((cause: unknown) => {
      if (active) showError(errorMessage(cause));
    });
    return () => { active = false; };
  }, [selected?.appId, showError]);

  function openApplication(application: Application, view: ConsoleView = "app-overview"): void {
    setSelected(application);
    navigate(view, application.appId);
  }

  function selectApplication(application: Application): void {
    openApplication(application, isApplicationScoped(route.view) ? route.view : "app-overview");
  }

  async function submitApplication(draft: ApplicationDraft): Promise<void> {
    setBusy(true);
    try {
      const editing = dialog === "edit" && selected !== null;
      const result = editing ? await updateApplication(selected, draft) : await createApplication(draft);
      if (!editing) {
        const created = result as Awaited<ReturnType<typeof createApplication>>;
        if (created.credentials !== undefined) setCredentials(created.credentials);
        navigate("app-overview", result.application.appId);
      }
      setDialog(null);
      setSelected(result.application);
      setEndpoints(result.endpoints);
      await refresh();
      showNotice(editing ? "应用配置已同步" : result.application.appType === "native" ? "原生应用已创建，请立即保存一次性 AppSecret" : "应用已创建，可以立即开始接入");
    } catch (cause) {
      showError(errorMessage(cause));
      throw cause;
    } finally {
      setBusy(false);
    }
  }

  async function toggleApplication(): Promise<void> {
    if (selected === null) return;
    setBusy(true);
    try {
      const result = await updateApplication(selected, { enabled: !selected.enabled });
      setSelected(result.application);
      setEndpoints(result.endpoints);
      await refresh();
      showNotice(result.application.enabled ? "应用已启用" : "应用已停用，新 Token 将无法访问 IM");
      setConfirmation(null);
    } catch (cause) {
      showError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function rotateCredentials(): Promise<void> {
    if (selected?.appType !== "native") return;
    setBusy(true);
    try {
      const result = await rotateApplicationCredentials(selected);
      setSelected(result.application);
      setEndpoints(result.endpoints);
      setCredentials(result.credentials);
      await refresh();
      showNotice("AppSecret 已轮换，请安全保存并更新业务服务端");
      setConfirmation(null);
    } catch (cause) {
      showError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="loading-page"><span className="loader" /><strong>正在连接开放平台</strong><p>正在加载应用和租户配置…</p></div>;
  if (session === null && loadSession() === null) {
    return <LoginPage error={error} onEnterpriseLogin={() => { void startLogin(); }} onPasswordLogin={async (input) => {
      setError(null);
      const authenticated = await authenticateWithPassword(input);
      setSession(authenticated);
      await refresh();
    }} />;
  }

  const scoped = isApplicationScoped(route.view);
  const routedApplication = route.appId === null
    ? null
    : applications.find((application) => application.appId === route.appId) ?? null;
  const currentApplication = scoped
    ? selected?.appId === route.appId ? selected : routedApplication
    : selected;
  const currentEndpoints = currentApplication?.appId === selected?.appId ? endpoints : null;
  const applicationMissing = scoped && currentApplication === null;
  const permission = requiredPermission(route.view);
  const unauthorized = permission !== undefined && currentApplication !== null && !currentApplication.permissions.includes(permission);

  return (
    <ConsoleShell
      application={currentApplication}
      applications={applications}
      developer={developer}
      navigationOpen={navigationOpen}
      permissions={currentApplication?.permissions ?? []}
      route={route}
      onCloseNavigation={() => { setNavigationOpen(false); }}
      onLogout={() => {
        void logout(loadSession()).then(() => {
          setSession(null);
          setDeveloper(null);
          saveSession(null);
        });
      }}
      onNavigate={(view) => { navigate(view, currentApplication?.appId ?? null); }}
      onOpenNavigation={() => { setNavigationOpen(true); }}
      onSelectApplication={selectApplication}
    >
      {notice === null ? null : <Banner kind="success" message={notice} onClose={() => { setNotice(null); }} />}
      {error === null ? null : <Banner kind="danger" message={error} onClose={() => { setError(null); }} />}

      {route.view === "applications" ? <ApplicationsView applications={applications} onCreate={() => { setDialog("create"); }} onSelect={openApplication} /> : null}
      {route.view === "not-found" || applicationMissing ? <NotFoundPage onBack={() => { navigate("applications"); }} /> : null}
      {!applicationMissing && unauthorized ? <NoPermissionPage onBack={() => { navigate("app-overview", currentApplication.appId); }} /> : null}

      {!applicationMissing && !unauthorized && currentApplication !== null ? <>
        {route.view === "app-overview" ? <ApplicationOverviewView application={currentApplication} endpoints={currentEndpoints} onEdit={() => { setDialog("edit"); }} onNavigate={(view) => { navigate(view, currentApplication.appId); }} /> : null}
        {route.view === "app-settings" ? <ApplicationSettingsView application={currentApplication} applications={applications} busy={busy} endpoints={currentEndpoints} onEdit={() => { setDialog("edit"); }} onSelect={selectApplication} onToggle={() => { if (currentApplication.enabled) setConfirmation("disable-application"); else void toggleApplication(); }} /> : null}
        {route.view === "credentials" ? <CredentialsSecurityView application={currentApplication} applications={applications} busy={busy} onRotate={() => { setConfirmation("rotate-credentials"); }} onSelect={selectApplication} /> : null}
        {route.view === "quickstart" ? <QuickstartView application={currentApplication} applications={applications} endpoints={currentEndpoints} onManageApplications={() => { navigate("applications"); }} onSelect={selectApplication} /> : null}
        {route.view === "users" ? <UserManagementView key={currentApplication.appId} application={currentApplication} applications={applications} onError={showError} onNotice={showNotice} onSelect={selectApplication} /> : null}
        {route.view === "system-accounts" ? <SystemAccountManagementView key={currentApplication.appId} application={currentApplication} applications={applications} onError={showError} onNotice={showNotice} onSelect={selectApplication} /> : null}
        {route.view === "groups" ? <GroupManagementView key={currentApplication.appId} application={currentApplication} applications={applications} onError={showError} onNotice={showNotice} onSelect={selectApplication} /> : null}
        {route.view === "message-send" ? <ServerMessageSendView key={currentApplication.appId} application={currentApplication} applications={applications} onError={showError} onNavigate={(view) => { navigate(view, currentApplication.appId); }} onNotice={showNotice} onSelect={selectApplication} /> : null}
        {route.view === "server-messages" ? <ServerMessagesView key={currentApplication.appId} application={currentApplication} applications={applications} onSelect={selectApplication} /> : null}
        {route.view === "callbacks" ? <CallbackConfigurationView key={currentApplication.appId} application={currentApplication} applications={applications} onError={showError} onNotice={showNotice} onSelect={selectApplication} /> : null}
        {route.view === "callback-failures" ? <CallbackFailureLogsView key={currentApplication.appId} application={currentApplication} applications={applications} onSelect={selectApplication} /> : null}
        {route.view === "retention" ? <RetentionPolicyView key={currentApplication.appId} application={currentApplication} applications={applications} onError={showError} onNotice={showNotice} onSelect={selectApplication} /> : null}
      </> : null}

      {dialog !== null ? <ApplicationDialog application={dialog === "edit" ? currentApplication ?? undefined : undefined} busy={busy} onClose={() => { setDialog(null); }} onSubmit={submitApplication} /> : null}
      {credentials === null ? null : <CredentialDialog credentials={credentials} onClose={() => { setCredentials(null); }} />}
      {confirmation === "rotate-credentials" ? <ConfirmDialog busy={busy} confirmLabel="确认轮换" consequences={["旧 AppSecret 签发的 UserSig 将立即失效", "业务服务端必须更新密钥", "受影响客户端需要重新登录"]} description="这是不可逆的安全操作，新 AppSecret 只展示一次。" title="轮换 AppSecret" onClose={() => { setConfirmation(null); }} onConfirm={() => { void rotateCredentials(); }} /> : null}
      {confirmation === "disable-application" ? <ConfirmDialog busy={busy} confirmLabel="确认停用" consequences={["停止签发新的登录票据", "现有票据失效后客户端无法重新连接", "可稍后重新启用应用"]} description="停用不会删除应用、历史消息或已有配置。" title="停用应用" onClose={() => { setConfirmation(null); }} onConfirm={() => { void toggleApplication(); }} /> : null}
    </ConsoleShell>
  );
}

function Banner({ kind, message, onClose }: { readonly kind: "success" | "danger"; readonly message: string; readonly onClose: () => void }) {
  return <div className={`notice notice-${kind}`} role={kind === "danger" ? "alert" : "status"}><span>{message}</span><button aria-label="关闭提示" type="button" onClick={onClose}>×</button></div>;
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
